import { useEffect, useRef } from "react";

const overviewData = [
  { label: "会社名", value: "株式会社iNeXX" },
  { label: "設立", value: "2023年（令和5年）11月" },
  { label: "資本金", value: "1,000,000円" },
  { label: "本社所在地", value: "広島県広島市西区三篠町1-7-32 房尾本店三篠ビル202" },
  { label: "営業所所在地", value: "高松営業所\n香川県高松市仏生山町甲75-6" },
  { label: "事業内容", value: "移動体通信業務 / 電気通信関連事業 / 電気設備及び監理等に係る工事請負" },
  { label: "登録検査事業者番号", value: "中点第0034号" },  
];

export default function CompanyOverview() {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".overview-el").forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = "1";
                (el as HTMLElement).style.transform = "translateY(0)";
              }, i * 60);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="company"
      ref={ref}
      className="py-24 relative overflow-hidden"
      style={{ background: "linear-gradient(180deg, #071530 0%, #0A1F44 100%)" }}
    >
      <div className="absolute top-0 left-0 right-0 h-px section-divider" />
      <div className="absolute -bottom-40 -right-40 w-96 h-96 rounded-full bg-inexx-blue/5 blur-3xl pointer-events-none" />

      <div className="max-w-5xl mx-auto px-4 md:px-8">
        {/* Section Title */}
        <div className="text-center mb-16">
          <p className="overview-el text-inexx-blue text-sm font-semibold tracking-widest uppercase mb-3 transition-all duration-500" style={{ opacity: 0, transform: "translateY(20px)" }}>
            Company Overview
          </p>
          <h2 className="overview-el text-3xl md:text-4xl font-bold text-white transition-all duration-500" style={{ opacity: 0, transform: "translateY(20px)" }}>
            会社概要
          </h2>
          <div className="overview-el mx-auto mt-4 w-16 h-0.5 bg-inexx-blue transition-all duration-500" style={{ opacity: 0 }} />
        </div>

        {/* Table */}
        <div
          className="overview-el rounded-2xl overflow-hidden transition-all duration-500"
          style={{
            opacity: 0,
            transform: "translateY(30px)",
            border: "1px solid rgba(0,119,204,0.25)",
          }}
        >
          {overviewData.map((row, i) => (
            <div
              key={row.label}
              className={`flex flex-col sm:flex-row transition-colors duration-200 hover:bg-inexx-blue/5 ${
                i < overviewData.length - 1 ? "border-b border-inexx-blue/15" : ""
              }`}
            >
              {/* Label */}
              <div
                className="sm:w-40 lg:w-48 flex-shrink-0 px-6 py-4 flex items-center"
                style={{ background: "rgba(0,119,204,0.08)" }}
              >
                <span className="text-inexx-blue-light text-sm font-semibold">{row.label}</span>
              </div>
              {/* Value */}
              <div className="flex-1 px-6 py-4 flex items-center"
                style={{ background: "rgba(13,41,82,0.3)" }}>
                <span className="text-gray-300 text-sm leading-relaxed whitespace-pre-line">{row.value}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Maps section */}
        <div
          className="overview-el mt-10 transition-all duration-500"
          style={{ opacity: 0, transform: "translateY(30px)" }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* 本社 */}
            <div
              className="rounded-2xl overflow-hidden"
              style={{ border: "1px solid rgba(0,119,204,0.25)" }}
            >
              <div
                className="px-6 py-3 flex items-center gap-2"
                style={{ background: "rgba(0,119,204,0.08)", borderBottom: "1px solid rgba(0,119,204,0.15)" }}
              >
                <i className="ri-map-pin-2-line text-inexx-blue text-base" />
                <span className="text-inexx-blue-light text-sm font-semibold">本社</span>
              </div>
              <div className="p-1" style={{ background: "rgba(13,41,82,0.3)" }}>
                <iframe
                  title="本社マップ"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3921.5978289013906!2d132.44986031184138!3d34.412252798521784!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x355a9897e6a09201%3A0xd23120f2e3cdb91!2z44CSNzMzLTAwMDMg5bqD5bO255yM5bqD5bO25biC6KW_5Yy66KW_5Yy65LiJ56-g55S677yR5LiB55uu77yX4oiS77yT77yS!5e0!3m2!1sja!2sjp!4v1777798466998!5m2!1sja!2sjp"
                  width="100%"
                  height="260"
                  style={{ border: 0, borderRadius: "8px", display: "block" }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
              <div
                className="px-6 py-3"
                style={{ background: "rgba(13,41,82,0.3)", borderTop: "1px solid rgba(0,119,204,0.1)" }}
              >
                <p className="text-gray-400 text-xs leading-relaxed">
                  広島県広島市西区三篠町1-7-32 房尾本店三篠ビル202
                </p>
              </div>
            </div>

            {/* 高松営業所 */}
            <div
              className="rounded-2xl overflow-hidden"
              style={{ border: "1px solid rgba(0,119,204,0.25)" }}
            >
              <div
                className="px-6 py-3 flex items-center gap-2"
                style={{ background: "rgba(0,119,204,0.08)", borderBottom: "1px solid rgba(0,119,204,0.15)" }}
              >
                <i className="ri-map-pin-2-line text-inexx-blue text-base" />
                <span className="text-inexx-blue-light text-sm font-semibold">高松営業所</span>
              </div>
              <div className="p-1" style={{ background: "rgba(13,41,82,0.3)" }}>
                <iframe
                  title="高松営業所マップ"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3148.946569837249!2d134.0405010774182!3d34.28614254528436!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3553ea750c93a3bb%3A0xba0b23d48966bf4d!2z55SyLe-8l--8lS3vvJYg5LuP55Sf5bGx55S6IOmrmOadvuW4giDpppnlt53nnIwgNzYxLTgwNzg!5e0!3m2!1sja!2sjp!4v1774669977220!5m2!1sja!2sjp"
                  width="100%"
                  height="260"
                  style={{ border: 0, borderRadius: "8px", display: "block" }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
              <div
                className="px-6 py-3"
                style={{ background: "rgba(13,41,82,0.3)", borderTop: "1px solid rgba(0,119,204,0.1)" }}
              >
                <p className="text-gray-400 text-xs leading-relaxed">
                  香川県高松市仏生山町甲75-6
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Careers section */}
        <div
          id="recruit"
          className="overview-el mt-20 transition-all duration-500"
          style={{ opacity: 0, transform: "translateY(30px)" }}
        >
          {/* Section Title */}
          <div className="text-center mb-12">
            <p className="text-inexx-blue text-sm font-semibold tracking-widest uppercase mb-3">
              Careers
            </p>
            <h2 className="text-[24px] md:text-4xl font-bold text-white mb-4 whitespace-nowrap">
              採用情報
            </h2>
            <p className="text-inexx-blue-light text-base md:text-lg font-medium mb-6">
              一緒に、通信インフラを支えませんか。
            </p>
            <div className="mx-auto w-16 h-0.5 bg-inexx-blue mb-6" />
            <p className="text-gray-400 text-sm md:text-base leading-relaxed max-w-2xl mx-auto">
              株式会社iNeXXでは、未経験者でも一人前のプロエンジニアになれるよう、教育体制や資格取得をサポートしています。通信業界で活躍したい方、ぜひご応募ください。
            </p>
          </div>

          {/* Job Category Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            {/* 中途採用 */}
            <div
              className="rounded-2xl p-6 md:p-8 flex flex-col items-center text-center transition-all duration-200 hover:scale-[1.02]"
              style={{
                background: "rgba(0,119,204,0.08)",
                border: "1px solid rgba(0,119,204,0.25)",
              }}
            >
              <div
                className="w-14 h-14 flex items-center justify-center rounded-full mb-5"
                style={{
                  background: "rgba(0,119,204,0.18)",
                  border: "1px solid rgba(0,119,204,0.4)",
                }}
              >
                <i className="ri-briefcase-4-line text-inexx-blue text-2xl" />
              </div>
              <h4 className="text-white text-lg md:text-xl font-bold mb-3">中途採用</h4>
              <p className="text-gray-400 text-sm leading-relaxed">
                即戦力・経験者歓迎。モバイルエンジニアや電波測定スタッフなど、専門スキルを活かせるポジションを募集しています。
              </p>
            </div>

            {/* アルバイト・パート */}
            <div
              className="rounded-2xl p-6 md:p-8 flex flex-col items-center text-center transition-all duration-200 hover:scale-[1.02]"
              style={{
                background: "rgba(0,119,204,0.08)",
                border: "1px solid rgba(0,119,204,0.25)",
              }}
            >
              <div
                className="w-14 h-14 flex items-center justify-center rounded-full mb-5"
                style={{
                  background: "rgba(0,119,204,0.18)",
                  border: "1px solid rgba(0,119,204,0.4)",
                }}
              >
                <i className="ri-time-line text-inexx-blue text-2xl" />
              </div>
              <h4 className="text-white text-lg md:text-xl font-bold mb-3">アルバイト・パート</h4>
              <p className="text-gray-400 text-sm leading-relaxed">
                土日祝休み・週休2日など、働きやすい環境を整えています。ドライバーや測定スタッフなど幅広く募集中です。
              </p>
            </div>

            {/* 業務委託 */}
            <div
              className="rounded-2xl p-6 md:p-8 flex flex-col items-center text-center transition-all duration-200 hover:scale-[1.02]"
              style={{
                background: "rgba(0,119,204,0.08)",
                border: "1px solid rgba(0,119,204,0.25)",
              }}
            >
              <div
                className="w-14 h-14 flex items-center justify-center rounded-full mb-5"
                style={{
                  background: "rgba(0,119,204,0.18)",
                  border: "1px solid rgba(0,119,204,0.4)",
                }}
              >
                <i className="ri-file-list-3-line text-inexx-blue text-2xl" />
              </div>
              <h4 className="text-white text-lg md:text-xl font-bold mb-3">業務委託</h4>
              <p className="text-gray-400 text-sm leading-relaxed">
                経験者向けの業務委託契約。スキルに応じた報酬設定で、自分のペースで活躍できます。
              </p>
            </div>
          </div>

          {/* CTA Button */}
          <div className="text-center">
            <a
              href="https://en-gage.net/nox/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 cursor-pointer whitespace-nowrap bg-inexx-blue hover:bg-inexx-blue-dark text-white font-semibold px-10 py-3.5 rounded-full text-sm transition-all duration-200 hover:scale-105"
            >
              <i className="ri-external-link-line" />
              求人情報を見る
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
